/**
 * State Storage Utility
 *
 * Manages localStorage for machine state with short unique IDs and TTL.
 * Provides graceful fallback if localStorage is unavailable.
 *
 * Storage Format:
 * - Key: `kiosk-state:{stateId}` (e.g., "kiosk-state:abc123")
 * - Value: JSON with { state: SerializedState, timestamp: number }
 * - TTL: 24 hours (auto-cleanup on access)
 *
 * ID Format:
 * - 8 characters: base52 (a-z, A-Z only, no digits)
 * - ~53 trillion combinations (52^8)
 * - Collision probability: negligible for dev/test scenarios
 *
 * Features:
 * - Works across tabs and browser sessions
 * - Automatic cleanup of expired entries (TTL-based)
 * - Graceful degradation if localStorage unavailable
 * - Size tracking for debugging
 *
 * Why localStorage over sessionStorage:
 * - sessionStorage is per-tab, breaks when copying URLs to new tabs
 * - localStorage persists across tabs and browser restarts
 * - TTL ensures we don't fill up storage indefinitely
 */

import { datadogLogs } from "@datadog/browser-logs";
import type { SerializedState } from "../machine/state-serializer";

const STORAGE_PREFIX = "kiosk-state:";
const STATE_ID_LENGTH = 8;
const MAX_STORED_STATES = 50; // Cleanup threshold
const STATE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface StoredStateEntry {
  state: SerializedState;
  timestamp: number;
}

/**
 * Generate a short unique ID for state storage
 *
 * Uses base52 (a-z, A-Z only) for URL-safe IDs that comply with EHR naming conventions
 *
 * @returns 8-character unique ID (e.g., "aBxKpmZQ")
 */
export function generateStateId(): string {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let id = "";

  for (let i = 0; i < STATE_ID_LENGTH; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return id;
}

/**
 * Check if localStorage is available
 *
 * Returns false in:
 * - Private/incognito mode (some browsers)
 * - SSR context
 * - Browser with storage disabled
 */
function isStorageAvailable(): boolean {
  try {
    const test = "__storage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Store serialized state in localStorage with unique ID and timestamp
 *
 * @param stateId - Unique identifier for this state
 * @param state - Serialized machine state
 * @returns true if stored successfully, false otherwise
 */
export function storeState(stateId: string, state: SerializedState): boolean {
  if (!isStorageAvailable()) {
    console.warn(
      "[StateStorage] localStorage unavailable, state not persisted",
    );
    return false;
  }

  // Hoisted so the catch can report the size of the write we dropped.
  let json = "";
  try {
    const key = `${STORAGE_PREFIX}${stateId}`;
    const entry: StoredStateEntry = {
      state,
      timestamp: Date.now(),
    };
    json = JSON.stringify(entry);

    localStorage.setItem(key, json);

    datadogLogs.logger.info("StateStorage stored state", {
      stateId,
      key,
      size: json.length,
      currentState: state.value,
      contextKeys: Object.keys(state.context),
      expiresAt: new Date(entry.timestamp + STATE_TTL_MS).toISOString(),
      tags: ["KIOSK", "STATE", "STORAGE"],
    });

    // Cleanup old/expired entries (but not the one we just stored)
    cleanupOldStates(stateId);

    return true;
  } catch (error) {
    // Day-1 legibility: surface WHAT was dropped, not a silent console.error.
    // Behavior unchanged — still returns false. See plans/day1-error-legibility.md.
    const isQuota =
      error instanceof DOMException &&
      (error.name === "QuotaExceededError" || error.code === 22);
    datadogLogs.logger.warn("StateStorage write failed", {
      stateId,
      op: "storeState",
      reason: isQuota ? "quota_exceeded" : "unknown",
      attemptedSize: json.length,
      currentState: state.value,
      contextKeys: Object.keys(state.context),
      errorName: error instanceof Error ? error.name : typeof error,
      tags: ["KIOSK", "STATE", "STORAGE", "QUOTA"],
    });
    return false;
  }
}

/**
 * Update existing state in localStorage (same ID, new state)
 * Used for jump-to-state to avoid creating new storage entries
 *
 * @param stateId - Existing unique identifier
 * @param state - Updated serialized machine state
 * @returns true if updated successfully, false otherwise
 */
export function updateState(stateId: string, state: SerializedState): boolean {
  if (!isStorageAvailable()) {
    console.warn(
      "[StateStorage] localStorage unavailable, state not persisted",
    );
    return false;
  }

  // Hoisted so the catch can report the size of the write we dropped (0 if the
  // throw happened before serialization).
  let json = "";
  try {
    const key = `${STORAGE_PREFIX}${stateId}`;

    // Check if state exists
    const existingJson = localStorage.getItem(key);
    if (!existingJson) {
      console.warn(
        "[StateStorage] State ID not found, storing as new:",
        stateId,
      );
      return storeState(stateId, state);
    }

    // Update with new state but keep original timestamp
    const existingEntry = JSON.parse(existingJson) as StoredStateEntry;
    const entry: StoredStateEntry = {
      state,
      timestamp: existingEntry.timestamp, // Keep original timestamp
    };
    json = JSON.stringify(entry);

    localStorage.setItem(key, json);

    console.log("[StateStorage] Updated state:", state.value);

    if (state.value === "loadHrts") {
      datadogLogs.logger.info("StateStorage updated state", {
        stateId,
        key,
        size: json.length,
        currentState: state.value,
        contextKeys: Object.keys(state.context),
        tags: ["KIOSK", "STATE", "STORAGE"],
      });
    }

    return true;
  } catch (error) {
    // Day-1 legibility: surface WHAT was dropped, not a silent console.error.
    // Behavior unchanged — still returns false. See plans/day1-error-legibility.md.
    const isQuota =
      error instanceof DOMException &&
      (error.name === "QuotaExceededError" || error.code === 22);
    datadogLogs.logger.warn("StateStorage write failed", {
      stateId,
      op: "updateState",
      reason: isQuota ? "quota_exceeded" : "unknown",
      attemptedSize: json.length,
      currentState: state.value,
      contextKeys: Object.keys(state.context),
      errorName: error instanceof Error ? error.name : typeof error,
      tags: ["KIOSK", "STATE", "STORAGE", "QUOTA"],
    });
    return false;
  }
}

/**
 * Retrieve serialized state from localStorage by ID
 * Checks TTL and returns null if expired
 *
 * @param stateId - Unique identifier
 * @returns Deserialized state or null if not found/expired
 */
export function retrieveState(stateId: string): SerializedState | null {
  if (!isStorageAvailable()) {
    console.warn("[StateStorage] localStorage unavailable");
    return null;
  }

  try {
    const key = `${STORAGE_PREFIX}${stateId}`;
    const json = localStorage.getItem(key);

    if (!json) {
      console.log("[StateStorage] No state found for ID:", stateId);
      return null;
    }

    const entry = JSON.parse(json) as StoredStateEntry;
    const now = Date.now();
    const age = now - entry.timestamp;

    // Check if expired
    if (age > STATE_TTL_MS) {
      console.warn(
        `[StateStorage] State expired (age: ${Math.round(
          age / 1000 / 60,
        )} minutes):`,
        stateId,
      );
      localStorage.removeItem(key); // Clean up expired entry
      return null;
    }

    console.log("[StateStorage] Retrieved state:", {
      stateId,
      key,
      age: `${Math.round(age / 1000 / 60)} minutes`,
      expiresIn: `${Math.round((STATE_TTL_MS - age) / 1000 / 60)} minutes`,
      currentState: entry.state.value,
      contextKeys: Object.keys(entry.state.context),
    });

    return entry.state;
  } catch (error) {
    console.error("[StateStorage] Failed to retrieve state:", error);
    return null;
  }
}

/**
 * Remove state from localStorage
 *
 * @param stateId - Unique identifier
 */
export function removeState(stateId: string): void {
  if (!isStorageAvailable()) return;

  try {
    const key = `${STORAGE_PREFIX}${stateId}`;
    localStorage.removeItem(key);
    console.log("[StateStorage] Removed state:", stateId);
  } catch (error) {
    console.error("[StateStorage] Failed to remove state:", error);
  }
}

/**
 * Clean up old/expired stored states
 *
 * Removes:
 * 1. Expired entries (older than TTL)
 * 2. Excess entries if over MAX_STORED_STATES (keeps most recent)
 *
 * This prevents localStorage from growing unbounded.
 *
 * @param excludeStateId - Optional state ID to exclude from cleanup (e.g., current session)
 */
export function cleanupOldStates(excludeStateId?: string): void {
  if (!isStorageAvailable()) return;

  try {
    const now = Date.now();
    const entries: Array<{ key: string; timestamp: number; stateId: string }> =
      [];

    // Find all kiosk state keys and their timestamps
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        const stateId = key.replace(STORAGE_PREFIX, "");
        try {
          const json = localStorage.getItem(key);
          if (json) {
            const entry = JSON.parse(json) as StoredStateEntry;
            entries.push({ key, timestamp: entry.timestamp, stateId });
          }
        } catch {
          // Invalid entry, mark for deletion
          entries.push({ key, timestamp: 0, stateId });
        }
      }
    }

    let removedCount = 0;

    // Remove expired entries (but skip current session)
    for (const entry of entries) {
      if (entry.stateId === excludeStateId) continue; // Skip current session

      const age = now - entry.timestamp;
      if (age > STATE_TTL_MS || entry.timestamp === 0) {
        localStorage.removeItem(entry.key);
        removedCount++;
      }
    }

    // If still over threshold, remove oldest entries (excluding current session)
    const remaining = entries.filter(
      (e) =>
        (now - e.timestamp <= STATE_TTL_MS && e.timestamp !== 0) ||
        e.stateId === excludeStateId, // Always keep current session
    );
    if (remaining.length > MAX_STORED_STATES) {
      // Sort by timestamp (oldest first)
      remaining.sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = remaining.length - MAX_STORED_STATES;
      for (let i = 0; i < toRemove; i++) {
        // Skip current session even when cleaning up excess
        if (remaining[i].stateId === excludeStateId) continue;
        localStorage.removeItem(remaining[i].key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      console.log(`[StateStorage] Cleaned up ${removedCount} state(s)`);
    }
  } catch (error) {
    console.error("[StateStorage] Failed to cleanup old states:", error);
  }
}

/**
 * Get all stored state IDs (for debugging)
 *
 * @returns Array of state IDs currently in storage
 */
export function listStoredStates(): string[] {
  if (!isStorageAvailable()) return [];

  try {
    const stateIds: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        stateIds.push(key.replace(STORAGE_PREFIX, ""));
      }
    }
    return stateIds;
  } catch {
    return [];
  }
}

/**
 * Clear all stored states (for testing/debugging)
 */
export function clearAllStates(): void {
  if (!isStorageAvailable()) return;

  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keys.push(key);
      }
    }

    for (const key of keys) {
      localStorage.removeItem(key);
    }

    console.log(`[StateStorage] Cleared ${keys.length} stored state(s)`);
  } catch (error) {
    console.error("[StateStorage] Failed to clear states:", error);
  }
}
